import { DecimalPipe, NgClass, PercentPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AssistantKey, TestResult } from '@vendasta/protos/ai_assistants/v1alpha1';
import { of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { EvaluatorResultDetailComponent } from '../evaluator-result-detail/evaluator-result-detail.component';
import { isVoiceTest, StatusMap, TestStatus } from '../../test-management/test-run.utils';
import {
  INITIAL_EVALUATOR_RESULTS_PAGE_SIZE,
  JobStat,
  JobsToBeDoneService,
  RESULT_QUERY_PARAM,
  mergeQueryParams,
} from '../jobs-to-be-done.service';

/** A scored result's outcome, in the past tense the rest of the job's detail is written in. */
const RESULT_OUTCOMES = new Map<string, TestStatus>([
  ...StatusMap,
  ['success', { ...StatusMap.get('success')!, name: 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULT_PASSED' }],
  ['failure', { ...StatusMap.get('failure')!, name: 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULT_FAILED' }],
]);

export interface JobDetailModalData {
  stat: JobStat;
  assistantKey: Partial<AssistantKey>;
  /** Parent run of the result to open on, from a link into one conversation. */
  openResultTestRunId?: string;
}

/**
 * What the modal asks its opener to do next: reopen this job in the create form,
 * or remove it. Both only apply to a job the assistant owns.
 */
export type JobDetailModalResult = 'edit' | 'delete' | undefined;

type ResultsState = {
  status: 'loading' | 'loaded' | 'error';
  results: TestResult[];
  nextCursor: string;
  hasMore: boolean;
};

const INITIAL_STATE: ResultsState = { status: 'loading', results: [], nextCursor: '', hasMore: false };

// Longest last-message preview a row shows before ellipsis.
const SNIPPET_MAX_LENGTH = 120;

@Component({
  selector: 'ai-job-detail-modal',
  imports: [
    NgClass,
    DecimalPipe,
    PercentPipe,
    MatDialogModule,
    MatButtonModule,
    MatDividerModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    GalaxyAlertModule,
    GalaxyLoadingSpinnerModule,
    EvaluatorResultDetailComponent,
    AiAssistantI18nModule,
  ],
  templateUrl: './job-detail-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './job-detail-modal.component.scss',
})
export class JobDetailModalComponent {
  private readonly dialogRef = inject(MatDialogRef<JobDetailModalComponent, JobDetailModalResult>);
  private readonly jobsToBeDoneService = inject(JobsToBeDoneService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly router = inject(Router);
  protected readonly data = inject<JobDetailModalData>(MAT_DIALOG_DATA);

  protected readonly statusMap = RESULT_OUTCOMES;

  /**
   * Widths of the placeholder snippet bars, as a percentage. The count is the most
   * rows the first page can hold — how many it actually holds is unknown until it
   * arrives — so the table never has to grow into place, though a short page
   * settles smaller. Varying the widths reads as text of differing lengths rather
   * than a block of identical bars.
   */
  protected readonly skeletonRows = [82, 64, 91, 55, 74].slice(0, INITIAL_EVALUATOR_RESULTS_PAGE_SIZE);

  /** Set while the user is reading one result; null shows the results list. */
  protected readonly selectedResult = signal<TestResult | null>(null);

  private readonly state = signal<ResultsState>(INITIAL_STATE);

  protected readonly results = computed(() => this.state().results);
  protected readonly loading = computed(() => this.state().status === 'loading');
  protected readonly errored = computed(() => this.state().status === 'error');
  protected readonly hasMore = computed(() => this.state().hasMore);

  /** True while a "load more" fetch is in flight over already-shown rows. */
  protected readonly loadingMore = signal(false);

  constructor() {
    // Nothing has been scored against the job, so there is nothing to fetch —
    // open straight into the empty state.
    if (this.data.stat.evaluatedCount === 0) {
      this.state.set({ status: 'loaded', results: [], nextCursor: '', hasMore: false });
      // A result link cannot resolve against a job with nothing scored, so it goes
      // rather than sitting in the URL pointing at nothing.
      if (this.data.openResultTestRunId) this.writeResultParam(null);
      return;
    }
    if (this.data.openResultTestRunId) {
      this.openLinkedResult(this.data.openResultTestRunId);
      return;
    }
    this.fetchPage('');
  }

  /** Opens on the result a link named, falling back to the list when it is gone. */
  private openLinkedResult(testRunId: string): void {
    this.jobsToBeDoneService
      .getEvaluatorResult(this.data.assistantKey, this.data.stat.testCase.id, testRunId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result) {
            this.selectedResult.set(result);
            return;
          }
          this.writeResultParam(null);
          this.fetchPage('');
        },
        error: () => this.state.set({ status: 'error', results: [], nextCursor: '', hasMore: false }),
      });
  }

  /** Closes, asking the opener to reopen this job in the create form. */
  protected requestEdit(): void {
    this.dialogRef.close('edit');
  }

  /** Closes, asking the opener to remove this job. The opener confirms first. */
  protected requestDelete(): void {
    this.dialogRef.close('delete');
  }

  protected isVoice(result: TestResult): boolean {
    return isVoiceTest(result.testType);
  }

  /** Last thing said in the scored conversation, as a one-line row preview. */
  protected snippet(result: TestResult): string {
    const messages = result.chatHistory ?? [];
    const last = messages[messages.length - 1]?.content?.trim() ?? '';
    if (last.length <= SNIPPET_MAX_LENGTH) return last;
    return `${last.slice(0, SNIPPET_MAX_LENGTH).trimEnd()}…`;
  }

  protected select(result: TestResult): void {
    this.selectedResult.set(result);
    this.writeResultParam(result.testRunId);
  }

  protected backToResults(): void {
    this.selectedResult.set(null);
    this.writeResultParam(null);
    // A link opens straight onto one result, leaving the list behind it unfetched.
    // Only that path leaves the state on its initial 'loading', so it is the signal
    // that nothing has been fetched yet.
    if (this.state().status === 'loading') this.fetchPage('');
  }

  /**
   * Puts the open result in the URL so a refresh or a pasted link lands back on it.
   * Merged, so it leaves the job param the list owns alone.
   */
  private writeResultParam(testRunId: string | null): void {
    mergeQueryParams(this.router, { [RESULT_QUERY_PARAM]: testRunId || null });
  }

  protected loadMore(): void {
    const cursor = this.state().nextCursor;
    if (!cursor || this.loadingMore()) return;
    this.loadingMore.set(true);
    this.fetchPage(cursor);
  }

  close(): void {
    this.dialogRef.close();
  }

  private fetchPage(cursor: string): void {
    const existing = cursor ? this.state().results : [];
    this.jobsToBeDoneService
      .listEvaluatorResults(this.data.assistantKey, this.data.stat.testCase.id, cursor)
      .pipe(
        map(
          (page): ResultsState => ({
            status: 'loaded',
            results: [...existing, ...page.results],
            nextCursor: page.nextCursor,
            hasMore: page.hasMore,
          }),
        ),
        // The service already retried an over-limit page smaller, so reaching here
        // means that failed too or the error was something else. The state is left
        // untouched — rows and cursor stay, so another click re-requests — and the
        // snack is what distinguishes a failed click from the end of the list.
        catchError(() => {
          if (cursor) {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.LOAD_MORE_ERROR');
            return of<ResultsState>(this.state());
          }
          return of<ResultsState>({ status: 'error', results: existing, nextCursor: '', hasMore: false });
        }),
        // Placed above startWith so only the real page clears the flag — the seed
        // emission below is synchronous and would otherwise end the paging state
        // before the request had been made.
        tap(() => this.loadingMore.set(false)),
        startWith<ResultsState>({ ...this.state(), status: cursor ? 'loaded' : 'loading' }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((next) => this.state.set(next));
  }
}
